/**
 * Catalog Service — industry-aware, tenant-scoped
 *
 * BUG FIXED: Previously seeded ALL tenants with hardcoded interior-design
 * items from PREBUILT_ITEMS regardless of selected industry.
 *
 * CORRECT BEHAVIOUR:
 *   1. No client-side seeding. The DB seed script / fn_onboard_tenant
 *      creates the right system items per industry.
 *   2. getItems() always filters by the current project's industry_id.
 *   3. Null industry_id on an item = universal (shows in every industry).
 *   4. The generic SupabaseAdapter is replaced with a direct query so we
 *      can pass industry_id as a DB-level filter, not a JS filter over all items.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/utils/logger';

// ── Types ─────────────────────────────────────────────────────────────────────
export interface CatalogItemDB {
  id: string;
  name: string;
  description: string | null;
  specifications: string | null;
  category: string;
  room_type: string | null;
  default_rate: number;
  default_uom: string;
  has_dimensions: boolean;
  min_rate: number | null;
  max_rate: number | null;
  is_active: boolean;
  is_custom: boolean;
  industry_id: string | null;
  tenant_id: string | null;
  tags: string[] | null;
  quality_score: number | null;
  origin_type?: 'system' | 'starter_clone' | 'user_created' | 'imported' | 'ai_generated' | 'quotation_promoted';
  ownership_type?: 'system' | 'tenant' | 'override';
  lifecycle_state?: 'draft' | 'active' | 'archived' | 'hidden' | 'system_locked' | 'deprecated';
  pricing_rules?: any;
  is_archived?: boolean;
  source_quotation_id?: string | null;
  source_line_item_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CatalogFilter {
  industry_id?: string | null;  // null = only universal items; undefined = all
  room_type?: string;
  category?: string;
  search?: string;
  limit?: number;
  includeHidden?: boolean;
}

// ── Catalogue Service ─────────────────────────────────────────────────────────
class CatalogService {

  /**
   * Fetch catalogue items using the secure backend RPC which resolves visibility,
   * hidden items, overrides, and industry scoping.
   */
  async getItems(filter?: CatalogFilter): Promise<CatalogItemDB[]> {
    try {
      const { data, error } = await supabase.rpc('get_tenant_catalog_items', {
        p_category: filter?.category || null,
        p_include_hidden: filter?.includeHidden || false
      });

      if (error) {
        logger.warn(`[Catalog] getItems RPC failed: ${error.message}`);
        return [];
      }

      let items = data as CatalogItemDB[];

      // In-memory filtering for search and limit since RPC doesn't handle them
      if (filter?.search) {
        const query = filter.search.toLowerCase();
        items = items.filter(item => 
          item.name.toLowerCase().includes(query) || 
          (item.description && item.description.toLowerCase().includes(query))
        );
      }

      if (filter?.limit) {
        items = items.slice(0, filter.limit);
      }

      return items;
    } catch (err) {
      logger.error(`[Catalog] getItems error:`, err);
      return [];
    }
  }

  async getItem(id: string): Promise<CatalogItemDB | null> {
    const { data, error } = await supabase
      .from('catalog_items')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    return data as CatalogItemDB;
  }

  async searchItems(query: string, industryId?: string): Promise<CatalogItemDB[]> {
    return this.getItems({ search: query, industry_id: industryId ?? undefined });
  }

  async addItem(
    item: Omit<CatalogItemDB, 'id' | 'created_at' | 'updated_at'>,
  ): Promise<CatalogItemDB | null> {
    const { data, error } = await supabase
      .from('catalog_items')
      .insert({ 
        ...item, 
        is_custom: true, 
        is_active: true,
        ownership_type: 'tenant',
        lifecycle_state: 'active'
      })
      .select()
      .maybeSingle();
    if (error) { logger.warn(`[Catalog] addItem failed: ${error.message}`); return null; }
    return data as CatalogItemDB;
  }

  async updateItem(id: string, updates: Partial<CatalogItemDB>): Promise<CatalogItemDB | null> {
    // Determine if we need to update directly or override
    const item = await this.getItem(id);
    if (!item) return null;

    if (item.tenant_id === null || item.ownership_type === 'system') {
      // Trigger Override Workflow
      const { data, error } = await supabase.rpc('override_catalog_item', {
        p_item_id: id,
        p_updates: updates
      });

      if (error) {
        logger.error(`[Catalog] Override workflow failed: ${error.message}`);
        return null;
      }
      return data as CatalogItemDB;
    }

    // Direct Update
    const { data, error } = await supabase
      .from('catalog_items')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .maybeSingle();
    if (error) { logger.warn(`[Catalog] updateItem failed: ${error.message}`); return null; }
    return data as CatalogItemDB;
  }

  async deleteItem(id: string): Promise<boolean> {
    const item = await this.getItem(id);
    if (!item) return false;

    if (item.tenant_id === null || item.ownership_type === 'system') {
      // Hide the preload item
      const { error } = await supabase.rpc('hide_catalog_item', { p_item_id: id });
      if (error) {
        logger.error(`[Catalog] Failed to hide system item: ${error.message}`);
        return false;
      }
      return true;
    }

    // Soft Archive for tenant-owned items
    const { error } = await supabase
      .from('catalog_items')
      .update({ lifecycle_state: 'archived', is_active: false })
      .eq('id', id);
      
    if (error) logger.warn(`[Catalog] soft deleteItem failed: ${error.message}`);
    return !error;
  }

  async deactivateItem(id: string): Promise<CatalogItemDB | null> {
    return this.updateItem(id, { is_active: false });
  }

  async promoteToCatalog(payload: Omit<CatalogItemDB, 'id' | 'created_at' | 'updated_at'>): Promise<CatalogItemDB | null> {
    const { data, error } = await supabase
      .from('catalog_items')
      .insert({
        ...payload,
        origin_type: 'quotation_promoted',
        is_custom: true,
        is_active: true
      })
      .select()
      .maybeSingle();

    if (error) { 
      logger.warn(`[Catalog] promoteToCatalog failed: ${error.message}`); 
      return null; 
    }
    return data as CatalogItemDB;
  }

  // Compat shims
  async saveDraft(_id: string): Promise<boolean> { return true; }
  isSaved(_id: string): boolean { return true; }
  getUnsavedItems(): CatalogItemDB[] { return []; }
  async syncAll(): Promise<boolean> { return true; }
  hasUnsavedChanges(): boolean { return false; }
  async refresh(filter?: CatalogFilter): Promise<CatalogItemDB[]> { return this.getItems(filter); }
}

export const catalogService = new CatalogService();
